import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type BulkInviteResourceV3,
  bulkActionResource,
  bulkDeleteResource,
  bulkInviteResources,
  bulkInviteResourcesV3,
  createResource,
  deleteResourceProfilePicture,
  fetchResourcesByPermissions,
  getAllResources,
  getResourceById,
  getResourceByIdV2,
  getResourcePermissions,
  getResourceProficiency,
  type ResourceBulkActionPayload,
  resourceResetPassword,
  updateResource,
  updateResourceActive,
  updateResourceLock,
  updateResourceProficiency,
  uploadProfilePicture,
} from "controller/api";
import type {
  ApiErrorType,
  BulkInviteResource,
  CreateResourceInput,
  FetchResourcesByPermissionsInput,
  GetQueryProps,
  UpdateResourceActiveInput,
  UpdateResourceInput,
  UploadProfilePictureInput,
} from "shared";
import { sentryLogger } from "utilz";
import { debugConsole } from "utilz/helpers";
import { teamMemberSkillsKey } from "../team-members/useTeamMemberSkills";

export const useGetAllResources = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  enabled = true,
}: GetQueryProps & { enabled?: boolean }) => {
  return useQuery({
    // Include every server-affecting param in the key so changing page size,
    // page number, sort or filter triggers a fresh fetch. Existing
    // invalidations that target `["resources"]` still work because React Query
    // treats that as a prefix match.
    queryKey: [
      "resources",
      searchQuery,
      pageNumber,
      perPageCount,
      sortColumn,
      sortDirection,
      JSON.stringify(filter ?? []),
    ],
    queryFn: () =>
      getAllResources({
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    enabled,
    keepPreviousData: true,
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllResources",
      }),
  });
};

/**
 * Infinite/paged variant of {@link useGetAllResources} for scroll-loaded
 * pickers (manager + board-link drawers). Each page is `{ result, totalCount }`;
 * `getNextPageParam` advances until the accumulated rows reach `totalCount`.
 * Flatten with `data?.pages.flatMap((p) => p.result)`.
 */
export const useGetAllResourcesInfinite = ({
  perPageCount = 50,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  enabled = true,
}: Omit<GetQueryProps, "pageNumber"> & { enabled?: boolean }) => {
  return useInfiniteQuery({
    queryKey: [
      "resources",
      "infinite",
      searchQuery,
      perPageCount,
      sortColumn,
      sortDirection,
      JSON.stringify(filter ?? []),
    ],
    queryFn: ({ pageParam = 1 }) =>
      getAllResources({
        perPageCount,
        pageNumber: pageParam,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    enabled,
    keepPreviousData: true,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce(
        (sum, page) =>
          sum + (Array.isArray(page?.result) ? page.result.length : 0),
        0,
      );
      const total = lastPage?.totalCount ?? 0;
      return loaded < total ? allPages.length + 1 : undefined;
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getAllResourcesInfinite",
      }),
  });
};

export const useGetResourceById = (resourceId: string) => {
  return useQuery({
    enabled: resourceId && resourceId !== null ? true : false,
    queryKey: ["resource", resourceId],
    queryFn: () => getResourceById(resourceId),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getResourceById",
      }),
  });
};

export const useGetResourceByIdV2 = (resourceId: string) => {
  return useQuery({
    enabled: resourceId && resourceId !== null ? true : false,
    queryKey: ["resource", resourceId],
    queryFn: () => getResourceByIdV2(resourceId),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getResourceByIdV2",
      }),
  });
};

export const useCreateResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateResourceInput) => createResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "createResource",
      }),
  });
};

export const useUpdateResource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateResourceData: UpdateResourceInput) =>
      updateResource(updateResourceData),
    onSuccess: (_, variables, __) => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
      queryClient.invalidateQueries(["dispatch-calendar"]);
      // This PATCH carries `role_profile_ids`, and a role profile GRANTS
      // skills — so saving the Overview tab can change the member's skill set
      // without any skills endpoint being called. Without this the Skills tab
      // kept serving the pre-change list, which is the inconsistency users hit
      // when they edited role profiles and switched tabs.
      //
      // Invalidated unconditionally, like the four keys above: `role_profile_ids`
      // is not modelled on `UpdateResourceInput` (the call site casts), so
      // testing for it here would be a string check that silently stops working
      // if the payload key is ever renamed. The cost is one small refetch.
      queryClient.invalidateQueries(teamMemberSkillsKey(variables.resourceId));
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateResource",
      });
    },
  });
};

export const useUploadProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uploadProfilePictureData: UploadProfilePictureInput) =>
      uploadProfilePicture(uploadProfilePictureData),
    onSuccess: (_, variables, __) => {
      queryClient.invalidateQueries(["resource", variables.resourceId]);
      queryClient.invalidateQueries(["resources"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "uploadProfilePicture",
      });
    },
  });
};

export const useDeleteResourceProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteResourceProfilePicture,
    onSuccess: (_, variables, __) => {
      queryClient.invalidateQueries(["resource", variables.resourceId]);
      queryClient.invalidateQueries(["resources"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "deleteResourceProfilePicture",
      });
    },
  });
};

export const useResourceResetPassword = () => {
  return useMutation({
    mutationFn: resourceResetPassword,
    onError: (error: ApiErrorType, variables, context) => {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "resourceResetPassword",
      });
    },
  });
};

export const useGetResourcePermissions = (resourceId: string) => {
  return useQuery({
    queryKey: ["resource", resourceId, "permissions"],
    queryFn: () => getResourcePermissions(resourceId),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getResourcePermissions",
      }),
  });
};

export const useGetResourceProficiency = (resourceId: string) => {
  return useQuery({
    queryKey: ["resource", resourceId, "proficiency"],
    queryFn: () => getResourceProficiency(resourceId),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "getResourceProficiency",
      }),
  });
};

export const useUpdateResourceProficiency = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: {
      resourceId: string;
      data: Record<string, unknown>;
    }) => updateResourceProficiency(values),
    onSuccess: (_, variables, __) => {
      queryClient.invalidateQueries([
        "resource",
        variables.resourceId,
        "proficiency",
      ]);
      queryClient.invalidateQueries(["resources"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateResourceProficiency",
      });
    },
  });
};

export const useBulkDeleteResources = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resources: { resource_ids: Array<string> }) =>
      bulkDeleteResource(resources),
    onSuccess: (_, ___, __) => {
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "bulkDeleteResource",
      });
    },
  });
};

/**
 * Bulk action across selected team members (endpoint #10). Covers
 * enable / disable / delete / update_manager / ai_routing / working_hours.
 */
export const useBulkActionResources = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ResourceBulkActionPayload) =>
      bulkActionResource(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError(error: ApiErrorType) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "bulkActionResource",
      });
    },
  });
};

export const useUpdateResourceActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateResourceActiveData: UpdateResourceActiveInput) =>
      updateResourceActive(updateResourceActiveData),
    onSuccess: (_, variables, __) => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError(error: ApiErrorType, variables, context) {
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "updateResourceStatus",
      });
    },
  });
};

export const useUpdateResourceLock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateResourceLock,
    onSuccess: () => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError: (err) => {
      debugConsole(err);
    },
  });
};

export const useFetchResourcesByPermissions = ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection,
  filter,
  searchQuery,
  customer_id,
  serviceType,
}: FetchResourcesByPermissionsInput) => {
  return useQuery({
    enabled: customer_id && customer_id !== null ? true : false,
    queryKey: [
      "resources",
      customer_id,
      serviceType,
      searchQuery,
      pageNumber,
      perPageCount,
      sortColumn,
      sortDirection,
      JSON.stringify(filter ?? []),
    ],
    queryFn: () =>
      fetchResourcesByPermissions({
        customer_id,
        serviceType,
        perPageCount,
        pageNumber,
        sortColumn,
        sortDirection,
        filter,
        searchQuery,
      }),
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "fetchResourcesByPermissions",
      }),
  });
};

export const useBulkInviteResources = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkInviteResource[]) => bulkInviteResources(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "bulkInviteResources",
      }),
  });
};

/**
 * Synchronous multi-invite (POST /v3/control-center/resource). Use this when
 * the UI needs the resources list to be refreshable immediately after submit
 * — unlike `useBulkInviteResources` which returns before the records are
 * committed (background processing).
 */
export const useBulkInviteResourcesV3 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: BulkInviteResourceV3[]) => bulkInviteResourcesV3(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["resource"]);
      queryClient.invalidateQueries(["resources"]);
      queryClient.invalidateQueries(["subscriptionDetails"]);
      queryClient.invalidateQueries(["activeCount"]);
    },
    onError: (error: ApiErrorType) =>
      sentryLogger({
        error: error,
        workflow: "controlCenter",
        level: "error",
        functionName: "bulkInviteResourcesV3",
      }),
  });
};
